import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Rating,
  CardActionArea,
  CardActions,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useNavigate } from "react-router-dom";
import useBoardSelection from "../../../../hooks/useBoardSelection";
import BoardSelectionMenus from "../../../../components/common/BoardSelectionMenus";

// Styled components
const StyledCard = styled(Card)(({ theme, viewed }) => ({
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6],
  },
  ...(viewed && {
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: 0,
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      margin: "8px",
    },
  }),
}));

const StyledRating = styled(Rating)({
  "& .MuiRating-iconFilled": {
    color: "#FFD700",
  },
  "& .MuiRating-iconEmpty": {
    color: "#CCCCCC",
  },
});

const AthleteSearchCard = ({
  athlete,
  onAddToBoard,
  onToggleFavorite,
  onViewProfile,
  viewed = false,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Use the board selection hook
  const boardSelection = useBoardSelection();

  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddToBoard = (event) => {
    boardSelection.handleBoardMenuClick(event);
    // Don't close the main menu here - let the board selection handle its own menu
  };

  const handleBoardAddSuccess = (boardId, columnId, boardName, columnName) => {
    const message = `Added ${athlete.firstName} ${athlete.lastName} to ${boardName} - ${columnName}`;
    setNotification({
      open: true,
      message,
      severity: "success",
    });

    // Call the original onAddToBoard if provided
    if (onAddToBoard) {
      onAddToBoard(athlete);
    }
  };

  const handleNavigateToBoard = (boardId) => {
    navigate(`/boards?boardId=${boardId}`);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleToggleFavorite = () => {
    onToggleFavorite(athlete);
    handleMenuClose();
  };

  const handleViewProfile = () => {
    onViewProfile(athlete);
    navigate(`/prospects/profile/${athlete.id}`);
    handleMenuClose();
  };

  // Format height from inches to feet and inches
  const formatHeight = (height) => {
    if (!height || height === 0) return "No data";
    // Convert string to number if needed
    const inches = parseInt(height);
    if (isNaN(inches)) return "No data";
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    return `${feet}'${remainingInches}"`;
  };

  // Format weight
  const formatWeight = (weight) => {
    if (!weight || weight === 0) return "No data";
    // Convert string to number if needed
    const weightNum = parseInt(weight);
    if (isNaN(weightNum)) return "No data";
    return `${weightNum} lbs`;
  };

  // Format forty time
  const formatFortyTime = (time) => {
    if (!time || time === 0) return null;
    return `40: ${time}s`;
  };

  return (
    <>
      <StyledCard viewed={viewed}>
        <CardActionArea onClick={handleViewProfile}>
          <CardMedia
            component="img"
            height="180"
            image={
              athlete.profilePicture ||
              `https://ui-avatars.com/api/?name=${athlete.firstName}+${athlete.lastName}&background=random`
            }
            alt={`${athlete.firstName} ${athlete.lastName}`}
          />
          <CardContent sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Typography variant="h6" component="div" noWrap>
                {athlete.firstName} {athlete.lastName}
              </Typography>
              <StyledRating
                name="athlete-rating"
                value={athlete.stars || 0}
                readOnly
                size="small"
                max={5}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Chip
                label={athlete.position || "No position"}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {athlete.highSchool || "No school"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              <Chip
                size="small"
                label={formatHeight(athlete.height)}
                variant="outlined"
              />
              <Chip
                size="small"
                label={formatWeight(athlete.weight)}
                variant="outlined"
              />
              {formatFortyTime(athlete.fortyTime) && (
                <Chip
                  size="small"
                  label={formatFortyTime(athlete.fortyTime)}
                  variant="outlined"
                />
              )}
            </Box>
          </CardContent>
        </CardActionArea>

        <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
          <Box>
            <Tooltip title="Add to Board">
              <IconButton
                size="small"
                onClick={handleAddToBoard}
                color="primary"
              >
                <PersonAddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                athlete.favorite ? "Remove from Favorites" : "Add to Favorites"
              }
            >
              <IconButton
                size="small"
                onClick={handleToggleFavorite}
                color={athlete.favorite ? "error" : "default"}
              >
                {athlete.favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
              </IconButton>
            </Tooltip>
          </Box>

          <Box>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={handleViewProfile}
            >
              View
            </Button>
            <IconButton
              size="small"
              onClick={handleMenuClick}
              aria-label="more options"
              aria-controls={open ? "athlete-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="athlete-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              MenuListProps={{
                "aria-labelledby": "more-button",
              }}
            >
              <MenuItem
                onClick={(event) => {
                  handleAddToBoard(event);
                  handleMenuClose();
                }}
              >
                Add to Board
              </MenuItem>
              <MenuItem onClick={handleToggleFavorite}>
                {athlete.favorite
                  ? "Remove from Favorites"
                  : "Add to Favorites"}
              </MenuItem>
              <MenuItem onClick={handleViewProfile}>View Profile</MenuItem>
              <MenuItem onClick={handleMenuClose}>Send Message</MenuItem>
              <MenuItem onClick={handleMenuClose}>Schedule Visit</MenuItem>
              <MenuItem onClick={handleMenuClose}>Add Notes</MenuItem>
            </Menu>
          </Box>
        </CardActions>
      </StyledCard>

      {/* Board Selection Menus - Rendered outside the card to avoid z-index issues */}
      <BoardSelectionMenus
        {...boardSelection}
        athlete={athlete}
        onAddToBoard={(athlete, boardId, columnId) =>
          boardSelection.handleAddToBoard(
            athlete,
            boardId,
            columnId,
            handleBoardAddSuccess
          )
        }
        onCreateBoard={(athlete) =>
          boardSelection.handleCreateBoard(athlete, handleBoardAddSuccess)
        }
        onNavigateToBoard={handleNavigateToBoard}
      />

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AthleteSearchCard;
